import { DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  tap,
} from "rxjs/operators";

import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { WeeklyPlanning } from "src/model/class/general classes/weekly-planning";
import { ClickActionType } from "src/model/enum/enumCustomInterface";
import { LogLevel } from "src/components/shared-components/enum/enumLog";

import {
  WeeklyPlanningResetKind,
  WeeklyPlanningWriteCase,
} from "src/model/enum/enumWeeklyPlanning";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { iWeeklyPlanning } from "src/model/interface/iWeekly-planning";
import { AddressService } from "src/service/address.service";
import { AlertService } from "src/service/alert.service";
import { ToastService } from "src/service/toast.service";
import { EnvironmentService } from "src/service/environment.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { PopupService } from "src/service/popup.service";
import { WeeklyPlanningService } from "src/service/weekly-planning.service";
import { canEditWeeklyPlanningOption } from "src/state/selectors/server-settings-selector";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { SearchAddressComponent } from "src/components/shared-components/components/general-components/search-address/search-address.component";
import { SelectionAddressOrAddressFunction } from "src/components/shared-components/enum/enumAddress";
import { EnvironmentSearchComponent } from "../environment-search/environment-search.component";
import { TimeSelectorComponent } from "../time-selector/time-selector.component";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { AddressMethod } from "src/components/shared-components/interfaces/address-method";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { ToastType } from "src/components/shared-components/enum/enumMisc";

@Component({
    selector: "app-weekly-planning-form",
    templateUrl: "./weekly-planning-form.component.html",
    styleUrls: ["./weekly-planning-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 *
 *
 * This component manages the simple modification of the weekly planning anagraph
 * The saving process is managed on update single field
 *
 */
export class WeeklyPlanningFormComponent
  implements OnInit, OnChanges, OnDestroy, iConnToolbarColour, iTranslation {
  private _logContext: string = "Weekly-planning-form.component";
  private _selectServerSettingsUserSub: Subscription = null;
  private _environmentSubscription: Subscription = null;
  private _selectedBuildingSubscription: Subscription = null;

  /** Area id of the current condominium: used to scope the address search to this building. */
  private _idArea: number = -1;

  private _translateSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;

  private _blockUpdate: boolean = false;

  /** True while a debounced auto-save is pending (a change happened but was not saved yet). */
  private _hasPendingSave: boolean = false;

  private _environmentAssociated: Environment = null;

  /**
   * `false` while the environment association is still unknown: the environment list is fetched
   * asynchronously, and until it lands {@link _environmentAssociated} cannot be resolved. The
   * auto-save must not read that gap as "no environment associated".
   */
  private _isEnvironmentResolved: boolean = false;
  private _listEnvironment: Array<Environment> = new Array<Environment>();

  private _isReadonlyModalOpen: boolean = false;

  //#region  Translated string
  private _lblMOp = "*";
  private _lblOp2 = "*";
  private _lblRead = "*";
  private _lblWrite = "*";
  private _lblWriteAddress = "*";
  private _lblValueToWrite = "*";
  private _lblRemoveValue = "*";
  private _lblNoEnviromentSelected = "*";
  private _lblSaveError = "*";

  public lblSelect: string = "";
  public lblCancel: string = "";
  public lblSelectAddress: string = "Seleziona un indirizzo";
  //#endregion

  public toolBarColor: string = "primary";

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public weeklyPlanningForm: UntypedFormGroup;
  public writeCase = WeeklyPlanningWriteCase;
  public resetKind = WeeklyPlanningResetKind;
  public addressWeeklyPlanning: Address = null;
  public placeholderValueString: string = "---";
  public nameAddress: string = "";
  public nameStateAddress: string = "";

  /** Default colour for each time-band, used when a planning has no colour saved. */
  private _defaultBandColors = {
    t1: "#08AB7D",
    t2: "#5bdea8",
    t3: "#137ea3",
    t4: "#80c3d3",
    t5: "#be8aed",
  };

  public color1: string = "";
  public color2: string = "";
  public color3: string = "";
  public color4: string = "";
  public color5: string = "";

  public value1: string = "";
  public value2: string = "";
  public value3: string = "";
  public value4: string = "";
  public value5: string = "";

  public isApp = false;
  public disable = true;

  /**
   * Whether the current user may edit the planning's registry data, mirroring
   * {@link canEditWeeklyPlanningOption}.
   *
   * Every other control is gated by enabling/disabling {@link weeklyPlanningForm}, which lets
   * Angular disable the underlying element and swallow its click. The two address fields render a
   * resolved name through `[value]` instead of a `formControlName`, so the form has no element of
   * theirs to disable: they need this flag explicitly. Denies until the settings have loaded, like
   * the selector backing it.
   *
   * Not to be confused with {@link disable}, which means "no address picked yet".
   */
  public canEdit = false;

  /**
   * True while the planning's main address is being resolved (an async GET). The values section
   * cannot render until the address lands, so the template holds a skeleton over it instead of
   * flashing the empty "select an address" state for a planning that already has one.
   */
  public isAddressLoading = false;

  @Input() CurrentWeeklyPlanning: WeeklyPlanning;

  constructor(
    private _fb: UntypedFormBuilder,
    private _weeklyPlanningService: WeeklyPlanningService,
    private _modalCtrl: ModalController,
    private _addressService: AddressService,
    private _alertService: AlertService,
    private _toastService: ToastService,
    private _platform: Platform,
    private _translateService: TranslateService,
    private _popupService: PopupService,
    private _enviromentService: EnvironmentService,
    private _loggerService: LoggerService,
    private _store: Store,
    public _networkService: NetworkService,
    public datepipe: DatePipe
  ) {
    this._platform.ready().then(async () => {
      this.isApp = this._platform.is("ios") || this._platform.is("android");
    });
  }

  ngOnInit() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnInit()",
      "START"
    );

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
    this.subscribeSelectedBuilding();
    this.loadListEnvironment();

    this.initializeForm();

    /* If the input planning is already set (editing an existing one), the first ngOnChanges
     * ran before the form existed and skipped the patch: apply it now that the form is built. */
    this.ngOnChanges();
  }

  ngOnDestroy() {
    /* Auto-save is debounced (600ms): if the user edits a field and immediately leaves the
     * editor (e.g. taps the back arrow), flush the pending save so the change is not lost. */
    if (this._hasPendingSave) {
      this._hasPendingSave = false;
      this.onSave();
    }

    this._networkServiceSubscription.unsubscribe();

    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }

    this.unsubscribeStoreSelect();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.undo",
        "dictionary.select",
        "dictionary.operation",
        "dictionary.operationOnAddress",
        "dictionary.read",
        "dictionary.write",
        "dictionary.writeAddress",
        "dictionary.valueToWrite",
        "dictionary.removeValueAsk",
        "dictionary.environmentNotAssociated",
        "dictionary.selectAddress",
        "dictionary.weeklyPlanningSaveError",
      ])
      .subscribe((translation) => {
        this.lblSelect = translation["dictionary.select"];
        this.lblCancel = translation["dictionary.undo"];
        this._lblMOp = translation["dictionary.operation"];
        this._lblOp2 = translation["dictionary.operationOnAddress"];
        this._lblRead = translation["dictionary.read"];
        this._lblWrite = translation["dictionary.write"];
        this._lblWriteAddress = translation["dictionary.writeAddress"];
        this._lblValueToWrite = translation["dictionary.valueToWrite"];
        this._lblRemoveValue = translation["dictionary.removeValueAsk"];
        this._lblNoEnviromentSelected =
          translation["dictionary.environmentNotAssociated"];
        this.lblSelectAddress = translation["dictionary.selectAddress"];
        this._lblSaveError = translation["dictionary.weeklyPlanningSaveError"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /**
   * Track the currently selected building/condominium so its area id can scope the address search
   * (only this condominium's datapoints are fetched).
   */
  private subscribeSelectedBuilding(): void {
    this._selectedBuildingSubscription = this._store
      .select(selectedBuilding)
      .pipe(filter((building: Building) => building !== null && building !== undefined))
      .subscribe((building: Building) => {
        this._idArea = building?.idAreaLocal ?? -1;
      });
  }

  private initializeForm() {
    this.placeholderValueString = "---";

    this.weeklyPlanningForm = this._fb.group(
      {
        name: new UntypedFormControl("", [Validators.required]),
        description: new UntypedFormControl(""),
        active: new UntypedFormControl(true, { updateOn: "change" }),
        idEnvironment: new UntypedFormControl("", {
          validators: [Validators.required],
          updateOn: "change",
        }),
        address: new UntypedFormControl("", [Validators.required]),
        writecase: new UntypedFormControl(this.writeCase.Always, {
          updateOn: "change",
        }),
        resetkind: new UntypedFormControl(this.resetKind.None, { updateOn: "change" }),
        resettimestring: new UntypedFormControl("00:00"),
        resetminutes: new UntypedFormControl(0),
        //commandAddress: new FormControl(""),
        stateAddress: new UntypedFormControl(""),
        t1: new UntypedFormControl(""),
        t2: new UntypedFormControl(""),
        t3: new UntypedFormControl(""),
        t4: new UntypedFormControl(""),
        t5: new UntypedFormControl(""),
        t1Color: new UntypedFormControl(this.color1),
        t2Color: new UntypedFormControl(this.color2),
        t3Color: new UntypedFormControl(this.color3),
        t4Color: new UntypedFormControl(this.color4),
        t5Color: new UntypedFormControl(this.color5),
        suspendOnExternalVariation: new UntypedFormControl(false, {
          updateOn: "change",
        }),
      },
      { updateOn: "blur" }
    );

    /* The registry data is editable only for who holds the permission; everyone else (residents,
     * and anybody until the settings have loaded) gets the same form read-only. Toggling with
     * emitEvent false: enable()/disable() emit on valueChanges, which would debounce straight
     * into onSave() and save the planning nobody touched. */
    this._selectServerSettingsUserSub = this._store
      .select(canEditWeeklyPlanningOption)
      .subscribe((canEditOption: boolean) => {
        this.canEdit = canEditOption;

        if (canEditOption) {
          this.weeklyPlanningForm.enable({ emitEvent: false });
        } else {
          this.weeklyPlanningForm.disable({ emitEvent: false });
        }
      });

    /**
     * On form field update, i wait if there aren't any emission for about 600ms
     * then i save new weekly planning data via API. valueChanges form field return an Observable object
     * that contain changed field: width distinctUntilChanged i discard same emission and debounceTime prevent
     * too fast form update.
     */
    this.weeklyPlanningForm.valueChanges
      .pipe(
        tap(() => (this._hasPendingSave = true)),
        distinctUntilChanged(),
        debounceTime(600)
      )
      .subscribe((changedFormValue) => {
        this._hasPendingSave = false;
        this.onSave();
      });
  }

  ngOnChanges() {
    this._blockUpdate = true;
    /* ngOnChanges fires before ngOnInit: when editing an existing planning the input is
     * already set on the first call, so the form does not exist yet. Guard against it (and
     * re-apply the patch from ngOnInit once the form is built). */
    if (this.CurrentWeeklyPlanning && this.weeklyPlanningForm) {

      /**
       * with patchValue function I update form field: there aren't expressed any field because
       * there is 1:1 relation between form field and object passed to patchValue.
       * emitEvent is set to false to prevent first emission during onChange event (during initialize form)
       *
       */
      this.weeklyPlanningForm.patchValue(this.CurrentWeeklyPlanning, {
        onlySelf: true,
        emitEvent: false,
      });
      if (this.CurrentWeeklyPlanning.resettimestring != "") {
        const timeSplit = this.CurrentWeeklyPlanning.resettimestring.split(" ");
        if (timeSplit.length > 1) {
          const hmsSplit = timeSplit[1].split(":");
          this.weeklyPlanningForm.patchValue(
            {
              resettimestring: hmsSplit[0] + ":" + hmsSplit[1],
            },
            {
              onlySelf: true,
              emitEvent: false,
            }
          );
        }
      }

      this.disable = this.weeklyPlanningForm.get("address").value == "";

      this.loadEnvironmentInfo();

      if (
        this.CurrentWeeklyPlanning.address != "" &&
        this.CurrentWeeklyPlanning.instanceguid != ""
      ) {
        /* Resolving the address is an async GET: mark the values area as loading so the template
         * shows a skeleton over it, instead of an empty area, until it lands. */
        this.isAddressLoading = true;
        this.loadAddressInfo(
          1,
          this.CurrentWeeklyPlanning.instanceguid,
          this.CurrentWeeklyPlanning.address
        );
      }

      if (
        this.CurrentWeeklyPlanning.instanceguidStateAddress != "" &&
        this.CurrentWeeklyPlanning.stateAddress != ""
      ) {
        this.loadAddressInfo(
          3,
          this.CurrentWeeklyPlanning.instanceguidStateAddress,
          this.CurrentWeeklyPlanning.stateAddress
        );
      }

      this.color1 = this.resolveBandColor(
        this.color1,
        this.CurrentWeeklyPlanning.t1Color,
        this._defaultBandColors.t1
      );
      this.color2 = this.resolveBandColor(
        this.color2,
        this.CurrentWeeklyPlanning.t2Color,
        this._defaultBandColors.t2
      );
      this.color3 = this.resolveBandColor(
        this.color3,
        this.CurrentWeeklyPlanning.t3Color,
        this._defaultBandColors.t3
      );
      this.color4 = this.resolveBandColor(
        this.color4,
        this.CurrentWeeklyPlanning.t4Color,
        this._defaultBandColors.t4
      );
      this.color5 = this.resolveBandColor(
        this.color5,
        this.CurrentWeeklyPlanning.t5Color,
        this._defaultBandColors.t5
      );
      this.saveColor();
    }

    this._blockUpdate = false;
  }

  /**
   * Open the popup to choose the address value on the base of the address kind and the available values
   * @param t
   */
  public async openAddressValue(t: number) {
    if (this._isReadonlyModalOpen) {
      return;
    }
    this._isReadonlyModalOpen = true;

    if (this.currentConnectionType != ConnectionType.NONE) {
      let commandToWrite: string = await this.openPopupSelectValueAddress(
        false
      );

      if (
        commandToWrite.trim() !== "" &&
        commandToWrite === ClickActionType.WRITE
      ) {
        const resultOperation = await this._alertService.presentPrompt(
          this._lblWrite,
          this._lblWriteAddress,
          this._lblValueToWrite,
          this.addressWeeklyPlanning.hiLevelDataFormat
        );
        if (resultOperation !== false) {
          commandToWrite = resultOperation.toString();
        } else {
          commandToWrite = "";
        }
      }
      if (commandToWrite !== "") {
        /* DP-1419: slot values may repeat across t1..t5. The backend accepts
         * duplicate values, so the front-end no longer enforces uniqueness:
         * whatever state the user picks is assigned to the slot as-is. */
        switch (t) {
          case 1:
            this.value1 = StaticClass.GetStateNameByValue(
              this.addressWeeklyPlanning.methods,
              commandToWrite
            );
            this.weeklyPlanningForm.patchValue({ t1: this.value1 });
            break;
          case 2:
            this.value2 = StaticClass.GetStateNameByValue(
              this.addressWeeklyPlanning.methods,
              commandToWrite
            );
            this.weeklyPlanningForm.patchValue({ t2: this.value2 });
            break;
          case 3:
            this.value3 = StaticClass.GetStateNameByValue(
              this.addressWeeklyPlanning.methods,
              commandToWrite
            );
            this.weeklyPlanningForm.patchValue({ t3: this.value3 });
            break;
          case 4:
            this.value4 = StaticClass.GetStateNameByValue(
              this.addressWeeklyPlanning.methods,
              commandToWrite
            );
            this.weeklyPlanningForm.patchValue({ t4: this.value4 });
            break;
          case 5:
            this.value5 = StaticClass.GetStateNameByValue(
              this.addressWeeklyPlanning.methods,
              commandToWrite
            );
            this.weeklyPlanningForm.patchValue({ t5: this.value5 });
            break;
          default:
            break;
        }
      } else {
        if (
          this.currentConnectionType == ConnectionType.WAN ||
          this.currentConnectionType == ConnectionType.LAN ||
          this.currentConnectionType == ConnectionType.LANWAN
        ) {
          const cancelOperation: boolean =
            await this._alertService.presentAlertConfirmOrDismiss(
              this._lblRemoveValue,
              "",
              ""
            );
          if (cancelOperation) {
            switch (t) {
              case 1:
                this.weeklyPlanningForm.patchValue({
                  t1: "",
                });
                this.value1 = "";
                break;
              case 2:
                this.weeklyPlanningForm.patchValue({
                  t2: "",
                });
                this.value2 = "";
                break;
              case 3:
                this.weeklyPlanningForm.patchValue({
                  t3: "",
                });
                this.value3 = "";
                break;
              case 4:
                this.weeklyPlanningForm.patchValue({
                  t4: "",
                });
                this.value4 = "";
                break;
              case 5:
                this.weeklyPlanningForm.patchValue({
                  t5: "",
                });
                this.value5 = "";
                break;
              default:
                break;
            }
          }
        }
      }
    } else {
      await this._alertService.presentAlertCommandNotAvaiable();
      await this._alertService.clearAlertCommandNotAvaiable();
    }

    const x =
      this.weeklyPlanningForm.get("t1").value == this.value1
        ? ""
        : this.value1 + "-";

    this._isReadonlyModalOpen = false;
  }

  /**
   * Opens the popup to select the time.
   */
  public async openTimeSelector() {
    if (this._isReadonlyModalOpen) { return; }
    this._isReadonlyModalOpen = true;

    const modalTimeSelector = await this._modalCtrl.create({
      component: TimeSelectorComponent,
      cssClass: "modal-time-selector",
      componentProps: {
        actualStartDatePicker: "",
        actualStartTimePicker:
          this.weeklyPlanningForm.get("resettimestring").value,
        isDate: false,
        isTime: true,
      },
      animated: true,
      showBackdrop: true,
    });

    modalTimeSelector.present();

    const { data } = await modalTimeSelector.onDidDismiss();

    if (modalTimeSelector) {
      modalTimeSelector.dismiss();
    }

    if (data && data.time) {
      this.weeklyPlanningForm.patchValue({
        resettimestring: data.time,
      });
    }
    this._isReadonlyModalOpen = false;
  }

  /**
   * Opens the popup to choose the environment to which to assign the weekly planning.
   */
  public async openEnvironmentSearch() {
    if (this._isReadonlyModalOpen) { return; }
    this._isReadonlyModalOpen = true;

    let applyChanges: boolean = false;
    let selectedEnvName: string = "";

    const modalSearchEnvironment = await this._modalCtrl.create({
      component: EnvironmentSearchComponent,
      cssClass: "modal-bigger",
      componentProps: { environments: this._listEnvironment },
    });

    modalSearchEnvironment.present();

    const { data } = await modalSearchEnvironment.onDidDismiss();

    if (modalSearchEnvironment) {
      modalSearchEnvironment.dismiss();
    }

    if (data) {
      //Effective selection has been done.
      if (data.id !== -1) {
        this._environmentAssociated = new Environment();
        this._environmentAssociated.id = data.id;
        this._environmentAssociated.descriptionEnvironment = data.name;
        selectedEnvName = data.name;
        applyChanges = true;

      } else {
        //Pop-up closed by clicking on "CANCEL" button; asking if the user desires to remove the association with the environment (if there is an environment associated)
        const cancelOperation: boolean =
          await this._alertService.presentAlertConfirmOrDismiss(
            this._lblRemoveValue,
            "",
            ""
          );
        if (cancelOperation) {
          this._environmentAssociated = null;
          selectedEnvName = "";
          applyChanges = true;
        }
      }
    } else {
      //POP-UP DISMISSED BY CLICKING OUTSIDE - DO NOTHING
    }

    if (applyChanges) {
      /* The user just stated the association explicitly: it is resolved by definition, whether or
       * not the environment list has landed yet. */
      this._isEnvironmentResolved = true;
      this.weeklyPlanningForm.patchValue({
        idEnvironment: selectedEnvName,
      });
    }
    this._isReadonlyModalOpen = false;
  }

  /**
   * Open the address search popup and on the close save the address on the relative field of the form
   */
  public async openAddressSearch(objectToChange: number) {
    /* The address inputs are not form members, so a disabled form does not stop their click:
     * without this the gate would hold everywhere but here. */
    if (!this.canEdit) { return; }
    if (this._isReadonlyModalOpen) { return; }
    this._isReadonlyModalOpen = true;

    /* Same scope as before: the whole building with its child areas. The complete address is
     * reloaded on confirm because the value pickers need its methods. */
    const modal = await this._modalCtrl.create({
      component: SearchAddressComponent,
      cssClass: "modal-bigger",
      componentProps: {
        idArea: this._idArea,
        includeChildArea: true,
        loadCompleteAddresses: true,
      },
    });

    modal.present();

    const { data } = await modal.onWillDismiss();

    if (modal) {
      modal.dismiss();
    }

    const selectedAddress: Address =
      data?.action == "confirm" &&
      data?.selectedType == SelectionAddressOrAddressFunction.ADDRESS &&
      data?.selectedValue
        ? new Address(data.selectedValue)
        : null;

    if (selectedAddress) {
      if (objectToChange == 1) {
        this.addressWeeklyPlanning = selectedAddress;

        this.weeklyPlanningForm.patchValue({
          address: selectedAddress.address,
        });
        this.CurrentWeeklyPlanning.instanceguid = selectedAddress.instanceGUID;
        this.nameAddress = selectedAddress.fullName;
        this.disable = this.weeklyPlanningForm.get("address").value == "";
      } /*else if (objectToChange == 2) {
        this.weeklyPlanningForm.patchValue({          
          commandAddress: data.selectedAddress
        });
        this.CurrentWeeklyPlanning.instanceguidCommandAddress = data.selectedInstanceGUID;
      }*/ else if (objectToChange == 3) {
        this.weeklyPlanningForm.patchValue({
          stateAddress: selectedAddress.address,
        });
        this.CurrentWeeklyPlanning.instanceguidStateAddress =
          selectedAddress.instanceGUID;
        this.nameStateAddress = selectedAddress.fullName;
      }
    }

    this._isReadonlyModalOpen = false;
  }

  /**
   * Save the new colors
   */
  /**
   * Resolve the colour to display for a time-band, null-safe against plannings that carry
   * null/undefined colour fields (older records). Prefers the planning colour, falls back to
   * the colour already resolved locally, then to the default band colour.
   *
   * @param currentColor the colour previously resolved for this band (empty on first load)
   * @param planningColor the colour stored on the planning (may be null/undefined/empty)
   * @param defaultColor the per-band default to use when no colour is available
   * @returns a non-empty colour string
   */
  private resolveBandColor(
    currentColor: string,
    planningColor: string,
    defaultColor: string
  ): string {
    if (planningColor && planningColor.length > 0) {
      return planningColor;
    }
    if (currentColor && currentColor.length > 0) {
      return currentColor;
    }
    return defaultColor;
  }

  public saveColor() {
    this.weeklyPlanningForm.patchValue(
      {
        t1Color: this.color1.includes("rgba")
          ? StaticClass.RGBAToHex(this.color1)
          : this.color1,
        t2Color: this.color2.includes("rgba")
          ? StaticClass.RGBAToHex(this.color2)
          : this.color2,
        t3Color: this.color3.includes("rgba")
          ? StaticClass.RGBAToHex(this.color3)
          : this.color3,
        t4Color: this.color4.includes("rgba")
          ? StaticClass.RGBAToHex(this.color4)
          : this.color4,
        t5Color: this.color5.includes("rgba")
          ? StaticClass.RGBAToHex(this.color5)
          : this.color5,
      },
      {
        onlySelf: true,
        emitEvent: false,
      }
    );
    this.onSave();
  }

  /**
   *
   * @param time date written as a string
   * @returns date written as a string with a different format (dd-MM-yyyy HH:mm:ss)
   *
   */
  public getDate(time: string): string {
    let timeSplit = time.split(":");
    const newDate = new Date(
      1,
      1,
      1,
      Number(timeSplit[0]),
      Number(timeSplit[1]),
      0,
      0
    );

    if (newDate.toString() != "Invalid Date") {
      time = this.datepipe.transform(newDate, "dd-MM-yyyy HH:mm:ss");
    }
    return time;
  }

  /**
   * Load the Environment object and its informations.
   */
  private loadEnvironmentInfo(): void {
    if (this.CurrentWeeklyPlanning) {
      if (this.CurrentWeeklyPlanning.idEnvironment > -1) {
        this._environmentAssociated = this._listEnvironment.find(
          (environment: Environment) =>
            environment.id === this.CurrentWeeklyPlanning.idEnvironment
        );
        this._isEnvironmentResolved = this._environmentAssociated != null;
      } else {
        this._environmentAssociated = this._listEnvironment.length > 0 ? this._listEnvironment[0] : null;
        this._isEnvironmentResolved = true;
      }

      this.weeklyPlanningForm.patchValue(
        {
          idEnvironment: this._environmentAssociated
            ? this._environmentAssociated.descriptionEnvironment
            : "",
        },
        {
          onlySelf: true,
          emitEvent: false,
        }
      );
    }
  }

  /**
   * Load the address object and its informations
   * @param value
   * @param instanceguid
   * @param address
   */
  public async loadAddressInfo(
    value: number,
    instanceguid: string,
    address: string
  ) {
    let addressSearchObj: iAddressSearch = {
      Instanceguid: instanceguid,
      Address: address,
      Name: "",
      Language: this._translateService.getCurrentLang(),
    };

    if (value == 1) {
      try {
        this.addressWeeklyPlanning =
          this._addressService.getAddressByInstanceGUIDAndAddress(
            instanceguid,
            address
          );
        if (!this.addressWeeklyPlanning || this.addressWeeklyPlanning == null) {
          const found: Address | Array<Address> = await this._addressService
            .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj);
          const result: Array<Address> = Array.isArray(found)
            ? found
            : found
              ? [found]
              : [];
          if (result.length > 0) {
            this.nameAddress = result[0].fullName;
            this.addressWeeklyPlanning = result[0];
          }
        } else {
          this.nameAddress = this.addressWeeklyPlanning.fullName;
        }
        if (this.addressWeeklyPlanning) {
          this.value1 = StaticClass.GetStateNameByValue(
            this.addressWeeklyPlanning.methods,
            this.CurrentWeeklyPlanning.t1
          );
          this.value2 = StaticClass.GetStateNameByValue(
            this.addressWeeklyPlanning.methods,
            this.CurrentWeeklyPlanning.t2
          );
          this.value3 = StaticClass.GetStateNameByValue(
            this.addressWeeklyPlanning.methods,
            this.CurrentWeeklyPlanning.t3
          );
          this.value4 = StaticClass.GetStateNameByValue(
            this.addressWeeklyPlanning.methods,
            this.CurrentWeeklyPlanning.t4
          );
          this.value5 = StaticClass.GetStateNameByValue(
            this.addressWeeklyPlanning.methods,
            this.CurrentWeeklyPlanning.t5
          );
        }
      } catch (error) {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "loadAddressInfo()",
          "Error resolving address: " + ((error as any)?.message ?? error)
        );
      } finally {
        this.isAddressLoading = false;
      }
    } else if (value == 3) {
      const addressStateWeeklyPlanning =
        this._addressService.getAddressByInstanceGUIDAndAddress(
          instanceguid,
          address
        );
      if (!addressStateWeeklyPlanning || addressStateWeeklyPlanning == null) {
        await this._addressService
          .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj)
          .then((result: Array<Address>) => {
            this.nameStateAddress = result[0].fullName;
          });
      } else {
        this.nameStateAddress = addressStateWeeklyPlanning.fullName;
      }
    }
  }

  /**
   * Save new weekly planning form calling api
   *
   */
  public async onSave() {
    if (!this._blockUpdate) {
      if (
        this.weeklyPlanningForm.get("resetkind").value === this.resetKind.AtTime
      ) {
        this.weeklyPlanningForm.patchValue(
          { resetminutes: 0 },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      } else if (
        this.weeklyPlanningForm.get("resetkind").value ===
        this.resetKind.AfterMinutes
      ) {
        this.weeklyPlanningForm.patchValue(
          { resettimestring: "00:00" },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      } else {
        this.weeklyPlanningForm.patchValue(
          {
            resettimestring: "00:00",
            resetminutes: 0,
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      }

      const weeklyPlanning: iWeeklyPlanning = this.weeklyPlanningForm.value;

      weeklyPlanning.resettimestring = this.getDate(
        weeklyPlanning.resettimestring
      );
      weeklyPlanning.instanceguid = this.CurrentWeeklyPlanning.instanceguid;
      //weeklyPlanning.instanceguidCommandAddress = this.CurrentWeeklyPlanning.instanceguidCommandAddress;
      weeklyPlanning.instanceguidStateAddress =
        this.CurrentWeeklyPlanning.instanceguidStateAddress;

      if (weeklyPlanning.address != "") {
        weeklyPlanning.t1 = this.formatCommand(weeklyPlanning.t1);
        weeklyPlanning.t2 = this.formatCommand(weeklyPlanning.t2);
        weeklyPlanning.t3 = this.formatCommand(weeklyPlanning.t3);
        weeklyPlanning.t4 = this.formatCommand(weeklyPlanning.t4);
        weeklyPlanning.t5 = this.formatCommand(weeklyPlanning.t5);
      }


      /* Only write the association once it is actually known: an auto-save that beats the
       * environment list would otherwise read "not resolved yet" as "none" and wipe it. */
      weeklyPlanning.idEnvironment = this._isEnvironmentResolved
        ? this._environmentAssociated
          ? this._environmentAssociated.id
          : -1
        : this.CurrentWeeklyPlanning.idEnvironment;

      let resetMinutes: number = Number(weeklyPlanning.resetminutes);
      weeklyPlanning.resetminutes = resetMinutes >= 0 ? resetMinutes : this.CurrentWeeklyPlanning.resetminutes;

      // Environment is mandatory: never persist a planning without one 
      if (this._isEnvironmentResolved && !this._environmentAssociated) {
        await this._toastService.showToast(
          ToastType.ERROR,
          this._lblNoEnviromentSelected,
          2000
        );
        return;
      }

      try {
        if (this.CurrentWeeklyPlanning.id === 0) {
          weeklyPlanning.active = false;
          const newWeeklyPlanning: WeeklyPlanning =
            await this._weeklyPlanningService.InsertweeklyPlanning(
              weeklyPlanning
            );

          this.CurrentWeeklyPlanning.id = newWeeklyPlanning.id;
        } else {
          await this._weeklyPlanningService.UpdateweeklyPlanning(
            this.CurrentWeeklyPlanning.id,
            weeklyPlanning
          );
        }
      } catch (error) {
        /* Auto-save is per-field and fire-and-forget: without surfacing the failure the user
         * would keep editing a form whose last change never reached the server, and only find
         * out on reload. Tell them, and log it. */
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "onSave()",
          "Save failed: " + ((error as any)?.message ?? error)
        );
        await this._toastService.showToast(
          ToastType.ERROR,
          this._lblSaveError,
          3500
        );
      }
    }
  }

  private formatCommand(command: string): string {
    if (command != "" && !Number(command)) {
      if (this.addressWeeklyPlanning.methods.includes("|")) {
        let cmds = this.addressWeeklyPlanning.methods.split("|");
        const index = cmds.findIndex((x) => x.includes(command));
        if (index >= 0) {
          let cmd = cmds[index].split("=");
          return cmd[1];
        }
      }
    }
    return command;
  }

  /**
   * Open popup value select and return selected value
   *
   * @returns
   */
  private async openPopupSelectValueAddress(
    actionWrite: boolean
  ): Promise<string> {
    const address: Address = new Address(this.addressWeeklyPlanning);
    let listMethods: Array<AddressMethod> = address.SplitMethods(
      address.loadMethodStringByLanguage(this._translateService.getCurrentLang())
    );

    for (let i = 0; i < listMethods.length; i++) {
      if (listMethods[i].value === undefined) {
        listMethods[i].value = ClickActionType.WRITE;
      }
    }

    if (actionWrite) {
      listMethods = [
        ...listMethods,
        {
          name: this._lblWrite,
          value: ClickActionType.WRITE,
        },
      ];
    }

    let commandToWrite: string =
      await this._popupService.showModalAddressMethods(
        this._lblMOp,
        this._lblOp2,
        listMethods
      );

    return (commandToWrite ??= "");
  }

  /**
   * Load the environment list and resolve the one associated to the current planning.
   *
   * Fetched directly from the API instead of through the shared environment store: the form only
   * needs each environment's id and description to map {@link WeeklyPlanning.idEnvironment} to a
   * name, so the heavy payload is skipped (`extraData = false` — no datapoints, no smart-component
   * info). The global store load requests `extraData = true` for every consumer, which is
   * needlessly heavy here.
   */
  private loadListEnvironment(): void {
    this._environmentSubscription = this._enviromentService
      .getEnvironments(this._idArea, null, false)
      .subscribe({
        next: (environmentObject: GenericApiData) => {
          this._listEnvironment = environmentObject?.data || [];
          this.loadEnvironmentInfo();
        },
        error: (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "loadListEnvironment()",
            error
          );
          this._listEnvironment = [];
          this.loadEnvironmentInfo();
        },
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectServerSettingsUserSub?.unsubscribe();
    this._environmentSubscription?.unsubscribe();
    this._selectedBuildingSubscription?.unsubscribe();
  }
  //#endregion
}
